import { useCallback, useEffect, useState } from "react";
import {
  PunchType,
  PunchSource,
  type Driver,
  type TimeclockPunch,
  type TimeclockTag,
} from "@eagledispatch/shared";
import { apiFetch, rawRequest } from "../../lib/api";
import Button from "../../components/Button";
import Card from "../../components/Card";
import FieldError from "../../components/FieldError";
import type { OfficeRoute } from "./OfficeHome";

const PAGE_SIZE = 25;

/** Local browser maxes for a manual punch (client hints; server enforces). */
const MAX_FUTURE_MS = 5 * 60 * 1000;
const MAX_PAST_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * #21.5 message shown when the office tries to tag an unverified (NO_TAG)
 * punch. Mirrors the server route guard contract.
 */
const NO_TAG_DISABLED_MESSAGE =
  "Unverified punches cannot be assigned a tag";

/** ISO datetime (from a datetime-local input) → browser-local Date, or null. */
function datetimeLocalToDate(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Browser-local Date → `YYYY-MM-DDTHH:mm` for a datetime-local input. */
function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** Local date key (YYYY-MM-DD) — hours are attributed to the IN's local date. */
function localDateKey(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatLocal(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface DailyHour {
  date: string;
  driverName: string;
  firstIn: string;
  lastOut: string | null;
  hours: number;
  lunchMinutes: number;
  openPunch: boolean;
  anomaly: boolean;
}

/**
 * Derived client-side hours: pair sequential IN→OUT per driver. An IN whose
 * next punch is also IN (or none) is an open punch. An OUT with no preceding
 * open IN is an anomaly. Cross-midnight pairs attribute to the IN's date.
 * Day boundary = LOCAL date of the IN punch.
 */
export function deriveDailyHours(punches: TimeclockPunch[]): DailyHour[] {
  const byDriver = new Map<string, TimeclockPunch[]>();
  for (const p of punches) byDriver.set(p.driverUserId, [...(byDriver.get(p.driverUserId) ?? []), p]);
  const rows: DailyHour[] = [];
  for (const [driverId, list] of byDriver) {
    const sorted = [...list].sort((a, b) => Date.parse(a.punchedAt) - Date.parse(b.punchedAt));
    const driverName = sorted[0]?.driverName ?? driverId;
    let openIn: TimeclockPunch | null = null;
    let lunchOut: TimeclockPunch | null = null;
    let lunchMinutes = 0;
    let lunchAnomaly = false;
    const finish = (out: TimeclockPunch | null) => {
      if (!openIn) return;
      const spanMinutes = out ? (Date.parse(out.punchedAt) - Date.parse(openIn.punchedAt)) / 60000 : 0;
      const netMinutes = Math.max(0, spanMinutes - lunchMinutes);
      rows.push({ date: localDateKey(openIn.punchedAt), driverName, firstIn: openIn.punchedAt,
        lastOut: out?.punchedAt ?? null, hours: Math.round(netMinutes / 6) / 10,
        lunchMinutes: Math.round(lunchMinutes * 10) / 10, openPunch: !out, anomaly: lunchAnomaly });
      openIn = null; lunchOut = null; lunchMinutes = 0; lunchAnomaly = false;
    };
    for (const punch of sorted) {
      if (punch.punchType === "IN") { if (openIn) finish(null); openIn = punch; }
      else if (punch.punchType === "LUNCH_OUT") { if (openIn && !lunchOut) lunchOut = punch; else lunchAnomaly = true; }
      else if (punch.punchType === "LUNCH_IN") {
        if (openIn && lunchOut) { lunchMinutes += (Date.parse(punch.punchedAt) - Date.parse(lunchOut.punchedAt)) / 60000; lunchOut = null; }
        else lunchAnomaly = true;
      } else if (punch.punchType === "OUT") {
        if (openIn) { if (lunchOut) lunchAnomaly = true; finish(punch); }
        else rows.push({ date: localDateKey(punch.punchedAt), driverName, firstIn: "", lastOut: punch.punchedAt, hours: 0, lunchMinutes: 0, openPunch: false, anomaly: true });
      }
    }
    if (openIn) finish(null);
  }
  rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return rows;
}

export default function Timeclock({
  onNavigate: _onNavigate,
  onToast,
}: {
  onNavigate: (_route: OfficeRoute) => void;
  onToast?: (msg: string, kind?: "success" | "error") => void;
}) {
  // ── Filters ──────────────────────────────────────────────────────────────
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driverFilter, setDriverFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // ── Punch log ────────────────────────────────────────────────────────────
  const [punches, setPunches] = useState<TimeclockPunch[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tags, setTags] = useState<TimeclockTag[]>([]);

  // ── Modals ───────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState<TimeclockPunch | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const loadDrivers = useCallback(() => {
    apiFetch<Driver[]>("/api/v1/drivers")
      .then(setDrivers)
      .catch(() => {
        // Non-fatal: filter dropdown falls back to empty.
      });
  }, []);

  const loadTags = useCallback(() => {
    apiFetch<TimeclockTag[]>("/api/v1/timeclock/tags")
      .then(setTags)
      .catch(() => {
        // Non-fatal: tag dropdown falls back to empty.
      });
  }, []);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(offset));
    if (driverFilter) params.set("driverUserId", driverFilter);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    return params.toString();
  }, [driverFilter, from, to, offset]);

  const loadPunches = useCallback(() => {
    setLoading(true);
    apiFetch<{ punches: TimeclockPunch[]; total: number }>(
      `/api/v1/timeclock/punches?${buildQuery()}`,
    )
      .then((res) => {
        setPunches(res.punches);
        setTotal(res.total);
        setError(null);
        setLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
  }, [buildQuery]);

  useEffect(() => {
    loadDrivers();
    loadTags();
  }, [loadDrivers, loadTags]);

  useEffect(() => {
    loadPunches();
  }, [loadPunches]);

  // ── CSV download (blob pattern) ──────────────────────────────────────────
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (driverFilter) params.set("driverUserId", driverFilter);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString();
      const res = await rawRequest(
        `/api/v1/timeclock/punches.csv${qs ? `?${qs}` : ""}`,
      );
      if (!res.ok) {
        let msg = `Export failed with status ${res.status}`;
        try {
          const body = (await res.json()) as { error?: string; details?: unknown };
          if (body?.error) msg = JSON.stringify(body);
        } catch {
          // ignore
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eagle-dispatch-timeclock-punches.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setDownloadError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  };

  // ── Manual add (POST /timeclock/punches) ─────────────────────────────────
  const [addDriverId, setAddDriverId] = useState("");
  const [addType, setAddType] = useState<PunchType>("IN");
  const [addPunchedAt, setAddPunchedAt] = useState("");
  const [addNote, setAddNote] = useState("");
  const [addErrors, setAddErrors] = useState<Record<string, string>>({});
  const [addSubmitting, setAddSubmitting] = useState(false);

  const nowLocalMinMax = {
    max: toDatetimeLocal(new Date(Date.now() + MAX_FUTURE_MS)),
    min: toDatetimeLocal(new Date(Date.now() - MAX_PAST_MS)),
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddErrors({});
    const errors: Record<string, string> = {};

    if (!addDriverId) {
      errors.driverUserId = "Driver is required";
    }
    if (!addPunchedAt) {
      errors.punchedAt = "Punch time is required";
    } else {
      const d = datetimeLocalToDate(addPunchedAt);
      if (!d) {
        errors.punchedAt = "Invalid punch time";
      } else if (d.getTime() > Date.now() + MAX_FUTURE_MS) {
        errors.punchedAt = "Punch timestamp cannot be in the future";
      } else if (d.getTime() < Date.now() - MAX_PAST_MS) {
        errors.punchedAt = "Punch timestamp cannot be older than 30 days";
      }
    }
    if (!addNote.trim()) {
      errors.note = "Note is required";
    } else if (addNote.trim().length > 500) {
      errors.note = "Note must be 500 characters or fewer";
    }

    if (Object.keys(errors).length > 0) {
      setAddErrors(errors);
      return;
    }

    setAddSubmitting(true);
    try {
      const d = datetimeLocalToDate(addPunchedAt)!;
      await apiFetch<TimeclockPunch>("/api/v1/timeclock/punches", {
        method: "POST",
        body: JSON.stringify({
          driverUserId: addDriverId,
          punchType: addType,
          punchedAt: d.toISOString(),
          note: addNote.trim(),
        }),
      });
      setShowAdd(false);
      setAddDriverId("");
      setAddType("IN");
      setAddPunchedAt("");
      setAddNote("");
      loadPunches();
      if (onToast) onToast("Punch added.");
    } catch (err: unknown) {
      const apiErr = err as { fieldErrors?: Record<string, string> };
      if (apiErr?.fieldErrors) {
        setAddErrors(apiErr.fieldErrors);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  // ── Edit modal (PATCH /timeclock/punches/:id) ────────────────────────────
  const [editType, setEditType] = useState<PunchType>("IN");
  const [editPunchedAt, setEditPunchedAt] = useState("");
  const [editTagId, setEditTagId] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editReason, setEditReason] = useState<"OTHER">("OTHER");
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  const openEdit = (punch: TimeclockPunch) => {
    setEditing(punch);
    setEditType(punch.punchType);
    setEditPunchedAt(toDatetimeLocal(new Date(punch.punchedAt)));
    setEditTagId(punch.tagId ?? "");
    setEditNote("");
    setEditReason("OTHER");
    setEditErrors({});
  };

  const closeEdit = () => {
    setEditing(null);
    setEditErrors({});
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditErrors({});
    const errors: Record<string, string> = {};

    if (!editPunchedAt) {
      errors.punchedAt = "Punch time is required";
    } else {
      const d = datetimeLocalToDate(editPunchedAt);
      if (!d) {
        errors.punchedAt = "Invalid punch time";
      } else if (d.getTime() > Date.now() + MAX_FUTURE_MS) {
        errors.punchedAt = "Punch timestamp cannot be in the future";
      } else if (d.getTime() < Date.now() - MAX_PAST_MS) {
        errors.punchedAt = "Punch timestamp cannot be older than 30 days";
      }
    }
    if (!editNote.trim()) {
      errors.note = "Note is required";
    } else if (editNote.trim().length > 500) {
      errors.note = "Note must be 500 characters or fewer";
    }

    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    setEditSubmitting(true);
    try {
      const d = datetimeLocalToDate(editPunchedAt)!;
      const patch: Record<string, unknown> = {
        punchType: editType,
        punchedAt: d.toISOString(),
        note: editNote.trim(),
        reason: editReason,
      };

      // Tag may only change on NFC/MANUAL punches (NO_TAG is unverified
      // forever — dropdown disabled, never sent). Only send when it actually
      // differs from the stored tag (empty = no change).
      if (editing && editing.source !== "NO_TAG") {
        if (editTagId && editTagId !== editing.tagId) {
          patch.tagId = editTagId;
        }
      }

      await apiFetch<TimeclockPunch>(`/api/v1/timeclock/punches/${editing!.id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      closeEdit();
      loadPunches();
      if (onToast) onToast("Punch updated.");
    } catch (err: unknown) {
      const apiErr = err as { fieldErrors?: Record<string, string> };
      if (apiErr?.fieldErrors) {
        setEditErrors(apiErr.fieldErrors);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setEditSubmitting(false);
    }
  };

  const hours = deriveDailyHours(punches);

  const sourceBadge = (source: PunchSource) => {
    if (source === "NO_TAG") {
      return <span className="tag tag-yellow">UNVERIFIED</span>;
    }
    if (source === "MANUAL") {
      return <span className="tag tag-blue">MANUAL</span>;
    }
    if (source === "LUNCH") {
      return <span className="tag tag-yellow">LUNCH</span>;
    }
    return <span className="tag tag-green">NFC</span>;
  };

  const typeLabels: Record<PunchType, string> = {
    IN: "In", OUT: "Out", LUNCH_OUT: "Lunch Out", LUNCH_IN: "Lunch In",
  };
  const typeBadge = (type: PunchType) => (
    <span className={`tag ${type === "IN" || type === "LUNCH_IN" ? "tag-green" : "tag-yellow"}`}>
      {typeLabels[type]}
    </span>
  );

  const hasMore = offset + punches.length < total;

  return (
    <main className="surface office-surface">
      <header className="surface-header">
        <div>
          <h1>Timeclock</h1>
          <p className="sub">Punch log, verification flags, and daily hours</p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <Button variant="secondary" onClick={() => setShowAdd(true)}>
            + Add Punch
          </Button>
          <Button
            variant="secondary"
            disabled={downloading}
            onClick={() => void handleDownload()}
          >
            {downloading ? "Generating…" : "Download CSV"}
          </Button>
        </div>
      </header>

      {downloadError && (
        <div className="banner banner-danger" style={{ marginBottom: "16px" }}>
          {downloadError}
        </div>
      )}

      {error && (
        <div className="banner banner-danger" style={{ marginBottom: "16px" }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="tcDriver">Driver</label>
            <select
              id="tcDriver"
              value={driverFilter}
              onChange={(e) => {
                setDriverFilter(e.target.value);
                setOffset(0);
              }}
            >
              <option value="">All drivers</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.userId}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="tcFrom">From</label>
            <input
              id="tcFrom"
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setOffset(0);
              }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="tcTo">To</label>
            <input
              id="tcTo"
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setOffset(0);
              }}
            />
          </div>
        </div>
        <p className="muted" style={{ fontSize: "12px", marginTop: "8px" }}>
          Times shown in your local timezone.
        </p>
      </Card>

      {/* Daily hours summary */}
      <Card>
        <h2>Daily Hours</h2>
        {hours.length === 0 ? (
          <p className="muted">No paired punches in the current filter.</p>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Driver</th>
                  <th>First In</th>
                  <th>Last Out</th>
                  <th>Hours</th>
                  <th>Lunch</th>
                  <th>Flags</th>
                </tr>
              </thead>
              <tbody>
                {hours.map((h, i) => (
                  <tr key={`${h.driverName}-${h.date}-${h.firstIn}-${i}`}>
                    <td>{h.date}</td>
                    <td>{h.driverName}</td>
                    <td>{h.firstIn ? formatLocal(h.firstIn) : "—"}</td>
                    <td>{h.lastOut ? formatLocal(h.lastOut) : "—"}</td>
                    <td>
                      {h.openPunch || h.anomaly ? "—" : h.hours.toFixed(1)}
                    </td>
                    <td>{h.lunchMinutes ? `${Math.round(h.lunchMinutes)}m${h.lunchMinutes > 45 ? " ⚠" : ""}` : "—"}</td>
                    <td>
                      {h.openPunch && (
                        <span className="tag tag-yellow">OPEN PUNCH</span>
                      )}
                      {h.anomaly && (
                        <span className="tag tag-red">ANOMALY</span>
                      )}
                      {!h.openPunch && !h.anomaly && (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Punch log table */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h2>Punch Log</h2>
          <span className="badge-count badge-reviewed" title="Total punches">
            {total}
          </span>
        </div>

        {loading ? (
          <p className="muted">Loading punches…</p>
        ) : punches.length === 0 ? (
          <div className="empty-state-box">
            <p className="muted">No punches match the current filter.</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date + Time</th>
                  <th>Driver</th>
                  <th>Type</th>
                  <th>Source</th>
                  <th>Tag</th>
                  <th>Entered By</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {punches.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => openEdit(p)}
                    style={{ cursor: "pointer" }}
                  >
                    <td title={p.punchedAt}>{formatLocal(p.punchedAt)}</td>
                    <td>{p.driverName ?? "—"}</td>
                    <td>{typeBadge(p.punchType)}</td>
                    <td>{sourceBadge(p.source)}</td>
                    <td>{p.tagLabel ?? "—"}</td>
                    <td>{p.createdByName ?? "—"}</td>
                    <td className="muted">{p.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && punches.length > 0 && (
          <div className="pagination">
            <Button
              variant="ghost"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ← Prev
            </Button>
            <span className="muted">
              {offset + 1}–{offset + punches.length} of {total}
            </span>
            <Button
              variant="ghost"
              disabled={!hasMore}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next →
            </Button>
          </div>
        )}
      </Card>

      {/* Manual add modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Punch</h3>
            <form onSubmit={handleAdd} className="form-stack">
              <div className="form-group">
                <label htmlFor="addDriver">Driver</label>
                <select
                  id="addDriver"
                  value={addDriverId}
                  onChange={(e) => setAddDriverId(e.target.value)}
                >
                  <option value="">Select driver…</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={d.userId}>
                      {d.name}
                    </option>
                  ))}
                </select>
                <FieldError message={addErrors.driverUserId} />
              </div>
              <div className="form-group">
                <label htmlFor="addType">Type</label>
                <select
                  id="addType"
                  value={addType}
                  onChange={(e) => setAddType(e.target.value as PunchType)}
                >
                  <option value="IN">IN</option>
                  <option value="OUT">OUT</option>
                  <option value="LUNCH_OUT">Lunch Out</option>
                  <option value="LUNCH_IN">Lunch In</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="addPunchedAt">Punch Time</label>
                <input
                  id="addPunchedAt"
                  type="datetime-local"
                  value={addPunchedAt}
                  min={nowLocalMinMax.min}
                  max={nowLocalMinMax.max}
                  onChange={(e) => setAddPunchedAt(e.target.value)}
                />
                <FieldError message={addErrors.punchedAt} />
                <p className="muted" style={{ fontSize: "12px", marginTop: "4px" }}>
                  Must be within the last 30 days and not in the future.
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="addNote">Note</label>
                <textarea
                  id="addNote"
                  value={addNote}
                  onChange={(e) => setAddNote(e.target.value)}
                  placeholder="Reason for manual entry"
                />
                <FieldError message={addErrors.note} />
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <Button type="submit" disabled={addSubmitting}>
                  {addSubmitting ? "Adding…" : "Save Punch"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Punch</h3>
            <form onSubmit={handleEdit} className="form-stack">
              <div className="form-group">
                <label htmlFor="editType">Type</label>
                <select
                  id="editType"
                  value={editType}
                  onChange={(e) => setEditType(e.target.value as PunchType)}
                >
                  <option value="IN">IN</option>
                  <option value="OUT">OUT</option>
                  <option value="LUNCH_OUT">Lunch Out</option>
                  <option value="LUNCH_IN">Lunch In</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="editPunchedAt">Punch Time</label>
                <input
                  id="editPunchedAt"
                  type="datetime-local"
                  value={editPunchedAt}
                  min={nowLocalMinMax.min}
                  max={nowLocalMinMax.max}
                  onChange={(e) => setEditPunchedAt(e.target.value)}
                />
                <FieldError message={editErrors.punchedAt} />
              </div>
              <div className="form-group">
                <label htmlFor="editTag">Tag</label>
                <select
                  id="editTag"
                  value={editTagId}
                  disabled={editing.source === "NO_TAG"}
                  onChange={(e) => setEditTagId(e.target.value)}
                >
                  <option value="">— No change —</option>
                  {tags
                    .filter((t) => t.isActive)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                </select>
                {editing.source === "NO_TAG" && (
                  <p className="muted" style={{ fontSize: "12px", marginTop: "4px" }}>
                    {NO_TAG_DISABLED_MESSAGE}
                  </p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="editReason">Reason</label>
                <select
                  id="editReason"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value as "OTHER")}
                >
                  <option value="OTHER">Other (note required)</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="editNote">Note</label>
                <textarea
                  id="editNote"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  placeholder="Reason for correction"
                />
                <FieldError message={editErrors.note} />
              </div>
              <p className="muted" style={{ fontSize: "12px" }}>
                Edits are logged with the reason above.
              </p>
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <Button type="submit" disabled={editSubmitting}>
                  {editSubmitting ? "Saving…" : "Save Changes"}
                </Button>
                <Button type="button" variant="ghost" onClick={closeEdit}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
